import pluralise from './common-pluralise-if-necessary';
import {
  createPaxStringFromRoomTariffs,
  createRoomStayCandidatesObj,
  getMultiRoomKey,
} from './common-utils';

interface HotelDetails {
  entireProperty?: boolean;
}

interface HotelDetail {
  isEntireProperty?: boolean;
}

interface HotelRoomInfo {
  title?: string;
}

enum ComboWrapperState {
  Occupancy,
}

type Nullable<T> = T | null;

export enum RoomCase {
  AddFlowButton = 'AddFlowButton',
  CartFlow = 'CartFlow',
  SingleRoom = 'SingleRoom',
}

interface RoomCaseArgsType {
  type: RoomCase;
  sellableType: string;
  mergedPropType: string;
  isEntireProperty: boolean;
  totalRoomCount: number;
  propertyTypeTag?: string;
  isBed?: boolean;
  isRoom?: boolean;
  selectRoomNew?: boolean;
  totalBedCount?: number;
}

export const getRoomStringWithPropertyCase = (
  sellableType: string,
  isEntireProperty: boolean,
  mergedPropType: string,
  propertyTypeTag: string,
  isPlural: boolean,
) => {
  if (sellableType) {
    if (isEntireProperty) {
      return isPlural ? `${propertyTypeTag}s` : propertyTypeTag;
    } else if (mergedPropType) {
      return isPlural ? `${mergedPropType}s` : mergedPropType;
    } else {
      return `${sellableType}${isPlural ? 's' : ''}`;
    }
  }
  return null;
};

export const isBedGetter = (sellableType: string) => {
  return sellableType.includes('bed');
};

/**
 * Generates a title for bedroom based on the provided options.
 * @param {Object} options - The options for generating the bedroom title.
 * @param {number} options.count - The number of beds or bedrooms.
 * @param {string} options.label - The singular form of 'bed' or 'bedroom'.
 * @param {boolean} [options.inDorm=false] - Flag to append 'in a Dorm' to the title.
 * @returns {string|null} - The formatted bedroom title or null if count is not provided.
 */
export const getBedroomTitle = ({
  count,
  label,
  appendText = '',
}: {
  count: number;
  label: string;
  appendText?: string;
}): string | null => {
  if (!count) return null; // Early return if count is not provided or falsy
  const pluralLabel = pluralise(label, count);
  return `${count} ${pluralLabel}${!!appendText ? ` ${appendText}` : ''}`;
};

export const multiRoomTextCreator = ({
  type,
  sellableType,
  isEntireProperty,
  totalRoomCount,
  propertyTypeTag,
  mergedPropType,
  isBed,
  isRoom = false,
  selectRoomNew,
  totalBedCount,
}: RoomCaseArgsType) => {
  const isPlural = totalRoomCount > 1;
  let roomCountWithText = `${totalRoomCount > 0 ? totalRoomCount : ''}`;
  let defaultRoomString = isPlural ? ' Rooms' : ' Room';
  let roomString = '';
  let roomStringWithPropertyCase = getRoomStringWithPropertyCase(
    sellableType,
    isEntireProperty,
    mergedPropType,
    propertyTypeTag,
    isPlural,
  );

  switch (type) {
    case RoomCase.AddFlowButton: {
      roomString = roomStringWithPropertyCase || defaultRoomString;
      return roomCountWithText.concat(' ', roomString);
    }

    case RoomCase.CartFlow: {
      if (propertyTypeTag) {
        // Handle the case where both rooms and beds are involved
        if (selectRoomNew && totalBedCount && totalRoomCount) {
          const remainingRoomCount = totalRoomCount - totalBedCount;
          let roomText = remainingRoomCount > 1 ? 'Rooms' : 'Room';
          let bedText = totalBedCount > 1 ? 'Beds' : 'Bed';

          if (remainingRoomCount > 0) {
            roomCountWithText = `${totalBedCount} ${bedText}, ${remainingRoomCount} ${roomText}`;
          } else {
            roomCountWithText = `${totalBedCount} ${bedText}`;
          }
          return roomCountWithText;
        }
        if (isBed && !propertyTypeTag.includes('bed') && !isRoom) {
          roomString = isPlural ? 'Beds' : 'Bed';
        } else if (isEntireProperty) {
          roomString = `${propertyTypeTag}${isPlural ? 's' : ''}`;
        } else if (mergedPropType) {
          roomString = `${mergedPropType}${isPlural ? 's' : ''}`;
        } else {
          roomString = defaultRoomString;
        }
      } else {
        roomString = defaultRoomString;
      }
      return roomCountWithText.concat(' ', roomString);
    }

    case RoomCase.SingleRoom: {
      if (totalRoomCount == 1) {
        roomCountWithText = ``;
      }
      roomString = roomStringWithPropertyCase || defaultRoomString;
      return roomCountWithText.concat(' ', roomString);
    }

    default: {
      return roomCountWithText.concat(' ', defaultRoomString);
    }
  }
};

export const getCancellationPolicyColor = policyType => {
  return policyType === 'NR' ? '#d0011b' : '#00b318';
};

export const descBlockCreatorForCombo = ({comboData, comboTariff, showPrice}) => {
  const rclObj = {};

  let lastPaxString = ''; // Renamed from paxString to clarify it retains the last value
  comboData?.forEach(item => {
    item?.ratePlan?.tariffs?.forEach(tarifData => {
      const pax = createPaxStringFromRoomTariffs(tarifData?.roomTariffs);
      lastPaxString = pax; // This will end up with the last pax processed
      const roomStayCandidatesObj = createRoomStayCandidatesObj(pax);
      const key = getMultiRoomKey(item?.roomCode, item?.ratePlan?.rpc, pax);

      rclObj[key] = {
        roomStayCandidatesObj,
        mtKey: tarifData?.mtKey,
        rtc: item?.roomCode,
        rpc: item?.ratePlan?.rpc,
        gsv: item?.ratePlan?.supplierCode,
        pax,
        isLucky: item?.ratePlan?.isLucky,
      };
    });
  });

  const paymode = comboTariff?.hermesData?.[0]?.paymentMode;
  const parentOfferCode = comboTariff?.hermesData?.[0]?.promoCode;

  return {
    comboPax: lastPaxString,
    comboRoomSelection: rclObj,
    comboPaymode: paymode,
    comboParentOfferCode: parentOfferCode,
  };
};
